import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@core/index";
import { GetTopicsCourseType } from "@packages/api/madlenAPI.schemas";
import { CourseData, isTurkishCourse } from "@packages/constant/CourseData";
import CourseSelector from "../../../assignments/AssignmentGenerator/components/course-selector";

export type CurriculumType = "MEB" | "INTERNATIONAL";
export type InternationalCurriculum = "AP" | "IGCSE" | "IB" | "ALEVEL";

export interface CurriculumSelectorProps {
  gradeLevel: number;
  themeColor: string;
  gradientClasses: Record<string, string>;
  labelClasses: Record<string, string>;
  curriculumType: CurriculumType;
  setCurriculumType: (type: CurriculumType) => void;
  internationalCurriculum: InternationalCurriculum;
  setInternationalCurriculum: (curriculum: InternationalCurriculum) => void;
  selectedCourse: GetTopicsCourseType;
  setSelectedCourse: (course: GetTopicsCourseType) => void;
  isMaarif?: boolean;
  additionalFilteredCourses?: GetTopicsCourseType[];
}

const CurriculumSelector: React.FC<CurriculumSelectorProps> = ({
  gradeLevel,
  themeColor,
  gradientClasses,
  labelClasses,
  curriculumType,
  setCurriculumType,
  internationalCurriculum,
  setInternationalCurriculum,
  selectedCourse,
  setSelectedCourse,
  isMaarif = false,
  additionalFilteredCourses = [],
}) => {
  const { t } = useTranslation();

  // Filter courses based on curriculum type and international curriculum
  const getFilteredCourses = (): GetTopicsCourseType[] => {
    if (curriculumType === "MEB") {
      return Object.keys(CourseData).filter(
        (key) =>
          isTurkishCourse(key) &&
          CourseData[key as GetTopicsCourseType].visible(gradeLevel),
      ) as GetTopicsCourseType[];
    } else {
      return Object.keys(CourseData).filter(
        (key) =>
          key.startsWith(`${internationalCurriculum}_`) &&
          CourseData[key as GetTopicsCourseType].visible(gradeLevel),
      ) as GetTopicsCourseType[];
    }
  };

  // Combine filtered courses with additional filtered courses
  const finalFilteredCourses =
    getFilteredCourses().length > 0
      ? Object.values(GetTopicsCourseType)
          .filter((course) => !getFilteredCourses().includes(course))
          .concat(additionalFilteredCourses)
      : additionalFilteredCourses;

  return (
    <div className="space-y-4">
      {/* Curriculum Type Selection */}
      <div className="space-y-2">
        <h4 className={`text-sm font-medium ${labelClasses[themeColor]}`}>
          {t("common.curriculumType")}
        </h4>
        <div className="grid grid-cols-2 gap-3">
          <Button
            type="button"
            onClick={() => setCurriculumType("MEB")}
            variant="solid"
            size="md"
            rounded="lg"
            textColor={
              curriculumType === "MEB"
                ? "text-white"
                : "text-gray-600 dark:text-gray-400"
            }
            bgColor={
              curriculumType === "MEB"
                ? `bg-gradient-to-r ${gradientClasses[themeColor]} shadow-lg`
                : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
            }
            fullWidth
          >
            <span className="text-sm font-medium">
              {t("common.nationalCurriculum")}
            </span>
          </Button>

          <Button
            type="button"
            onClick={() => setCurriculumType("INTERNATIONAL")}
            variant="solid"
            size="md"
            rounded="lg"
            textColor={
              curriculumType === "INTERNATIONAL"
                ? "text-white"
                : "text-gray-600 dark:text-gray-400"
            }
            bgColor={
              curriculumType === "INTERNATIONAL"
                ? `bg-gradient-to-r ${gradientClasses[themeColor]} shadow-lg`
                : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
            }
            fullWidth
          >
            <span className="text-sm font-medium">
              {t("common.internationalCurriculum")}
            </span>
          </Button>
        </div>
      </div>

      {/* International Curriculum Sub-selection */}
      {curriculumType === "INTERNATIONAL" && (
        <div className="space-y-2">
          <h4 className={`text-sm font-medium ${labelClasses[themeColor]}`}>
            {t("common.selectInternationalProgram")}
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {(["AP", "IGCSE", "IB", "ALEVEL"] as InternationalCurriculum[]).map(
              (curriculum) => (
                <Button
                  key={curriculum}
                  type="button"
                  onClick={() => setInternationalCurriculum(curriculum)}
                  variant="solid"
                  size="md"
                  rounded="lg"
                  textColor={
                    internationalCurriculum === curriculum
                      ? "text-white"
                      : "text-gray-600 dark:text-gray-400"
                  }
                  bgColor={
                    internationalCurriculum === curriculum
                      ? `bg-gradient-to-r ${gradientClasses[themeColor]} shadow-lg`
                      : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                  }
                  fullWidth
                >
                  <span className="text-sm font-medium">{curriculum}</span>
                </Button>
              ),
            )}
          </div>
        </div>
      )}

      {/* Course Selection */}
      <div className="space-y-2">
        <h4 className={`text-sm font-medium ${labelClasses[themeColor]}`}>
          {t("common.selectCourse")}
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <CourseSelector
            selectedGrade={gradeLevel}
            selectedCourse={selectedCourse}
            filteredCourses={finalFilteredCourses}
            setSelectedCourse={setSelectedCourse}
          />
        </div>
      </div>
    </div>
  );
};

export default CurriculumSelector;
